import { cn } from '@shared/lib/utils'
import { STEP_PROGRESS_COLORS } from './form-styles'

interface StepItem {
  key: string
  label: string
  shortLabel?: string
  isOptional?: boolean
}

interface StepProgressProps {
  currentStepIndex: number
  steps: StepItem[]
  highestStepReached?: number
  onStepChange?: (stepIndex: number) => void
  className?: string
}

export function StepProgress({
  currentStepIndex,
  steps,
  highestStepReached,
  onStepChange,
  className,
}: StepProgressProps) {
  const totalSteps = steps.length
  const maxReached = highestStepReached ?? currentStepIndex

  return (
    <div className={cn('bg-white px-2 md:px-3 lg:px-6 py-3 md:py-4 shadow-[0_1px_0_rgba(0,0,0,0.05)]', className)}>
      <div className="relative flex items-end justify-between gap-1 md:gap-2 lg:gap-4 overflow-x-auto">
        <div
          className="pointer-events-none absolute left-[6%] right-[6%] bottom-[10px] md:bottom-[6px] h-[2px] md:h-[3px] lg:h-[4px]"
          style={{ backgroundColor: STEP_PROGRESS_COLORS.line }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute left-[6%] bottom-[10px] md:bottom-[6px] h-[2px] md:h-[3px] lg:h-[4px]"
          style={{
            backgroundColor: STEP_PROGRESS_COLORS.completed,
            width: `${(maxReached / Math.max(totalSteps - 1, 1)) * 88}%`,
          }}
          aria-hidden
        />
        {steps.map((step, index) => {
          const isCurrentStep = index === currentStepIndex
          const isCompleted = index < currentStepIndex
          const isReachable = index <= maxReached
          const status = isCurrentStep ? 'current' : isCompleted ? 'done' : index <= maxReached ? 'visited' : 'upcoming'
          
          return (
            <button
              key={step.key}
              type="button"
              className={cn(
                'relative z-[1] flex flex-1 min-w-0 items-center justify-center bg-transparent',
                isReachable && !isCurrentStep ? 'cursor-pointer' : 'cursor-default'
              )}
              onClick={() => {
                if (isReachable && !isCurrentStep && onStepChange) {
                  onStepChange(index)
                }
              }}
              disabled={!isReachable || isCurrentStep}
              aria-label={`Step ${step.label}`}
            >
              <div className="flex flex-col items-center gap-0 md:gap-2">
                <span
                  className="hidden md:block text-sm leading-none md:max-w-[65px] lg:max-w-none truncate lg:whitespace-nowrap text-center"
                  style={{
                    color: isCurrentStep 
                      ? STEP_PROGRESS_COLORS.currentText 
                      : isReachable 
                        ? STEP_PROGRESS_COLORS.completedText 
                        : STEP_PROGRESS_COLORS.upcomingText
                  }}
                >
                  <span className="hidden lg:inline">{step.label}</span>
                  <span className="inline lg:hidden">{step.shortLabel || step.label}</span>
                  {step.isOptional && <span className="text-gray-400"> (Optional)</span>}
                </span>
                <div
                  className="flex h-5 w-5 items-center justify-center rounded-full border-2"
                  style={{
                    borderColor: isCurrentStep 
                      ? STEP_PROGRESS_COLORS.current 
                      : isReachable 
                        ? STEP_PROGRESS_COLORS.completed 
                        : STEP_PROGRESS_COLORS.upcoming,
                    backgroundColor: isCurrentStep ? STEP_PROGRESS_COLORS.current : 'white',
                  }}
                >
                  {status === 'done' || status === 'visited' ? <span className="sr-only">Completed</span> : null}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
